import { useState, useCallback, useMemo, useContext } from "react";
import api from "api/";
import { Store } from "store/database";
import reduxStore from "store";
import { handleServerError } from "lib/providers/auth-provider/actions";

/**
 * Binds a request to make to the server using the fetch api and the loading, error, and fetching logic
 * @param path - the path of the api call
 * @param params - default params to use on fetch api
 * @param setLoading - the setter for loading
 * @param setError - the setter for error
 * @param statusCode - the code that matches success, default is 200
 * @returns {Promise<*>} result of response.json() or error
 */
const makeRequestBinding = (
  loading,
  error,
  setLoading,
  setError,
  statusCode = 200,
  defaultParams
) => async (path, params = {}, id) => {
  try {
    // set loading and clean error
    if (!!id) {
      setLoading({ ...loading, [id]: true });
      setError({ ...error, [id]: null });
    } else {
      setLoading(true);
      setError(null);
    }

    const mergedParams = { ...defaultParams, ...params };

    // do the api call
    const response = await api.fetch(path, mergedParams);

    // no response, but ok
    if (response.status === 204) {
      return { ok: true };
    }

    //check status code
    if (response.status === statusCode) {
      return await response.json(); //status code match with default 200 or custom code
    } else {
      //let clone = response.clone();
      try {
        const e = await response.json();
        await reduxStore.dispatch(handleServerError(null, e));

        return e;
        //throw new Error(JSON.stringify(e))
      } catch (e) {
        return {
          error: e,
          message: "Request failed"
        };
        //throw new Error(JSON.stringify(await clone.text()))
      }
    }
  } catch (e) {
    if (!!id) {
      setError({ ...error, [id]: e.message });
    } else {
      setError(e.message); //error on fetch or json
    }
    throw new Error(e);
  } finally {
    if (!!id) {
      setLoading({ ...loading, [id]: false });
    } else {
      setLoading(false); //finally set loading falt
    }
  }
};

const defaultTransformFunction = (data, alias) =>
  Array.isArray(data.data)
    ? data.data.reduce(function(map, entity) {
        if (entity.id) {
          map[entity.id] = entity;
        } else {
          map[entity[alias]] = { ...entity, __alias__: alias };
        }
        return map;
      }, {})
    : { [data.id]: data };

/**
 * Default config for the entity
 * @type {{deleteDataStatusCode: number, putDataStatusCode: number, fetchDataStatusCode: number, patchDataStatusCode: number}}
 */
const defaultConfig = {
  transformFunction: defaultTransformFunction,
  fetchDataStatusCode: 200,
  getDetailDataStatusCode: 200,
  patchDataStatusCode: 200,
  putDataStatusCode: 200,
  deleteDataStatusCode: 200,
  disableDataStatusCode: 200,
  enableDataStatusCode: 200
};

/**
 * Creates the base for the entity
 * @param config - config like {
  fetchDataStatusCode: 200,
  getDetailDataStatusCode: 200,
  patchDataStatusCode: 200,
  putDataStatusCode: 200,
  deleteDataStatusCode: 200,
}
 * @returns {Promise<{data: {}, putting: boolean, deletingError: unknown, fetching: boolean, patchingError: unknown, patching: boolean, fetchingError: unknown, patchData: *, fetchData: *, deleting: boolean, deleteData: *, putData: *, puttingError: unknown}>}
 */
const useEntity = (entityName, alias, config = defaultConfig) => {
  const { database, dispatch } = useContext(Store);

  // fetching page or alot of data
  const [fetching, setFetching] = useState(null);
  const [fetchingError, setFetchingError] = useState(null);

  // to get only one
  const [getting, setGetting] = useState(null);
  const [gettingError, setGettingError] = useState(null);

  // to edit/patch entity
  const [patching, setPatching] = useState(null);
  const [patchingError, setPatchingError] = useState(null);

  // to create entity
  const [putting, setPutting] = useState(null);
  const [puttingError, setPuttingError] = useState(null);

  // to create entity
  const [deleting, setDeleting] = useState(null);
  const [deletingError, setDeletingError] = useState(null);

  // to disable entity
  const [disabling, setDisabling] = useState(null);
  const [disablingError, setDisablingError] = useState(null);

  // to enable entity
  const [enabling, setEnabling] = useState(null);
  const [enablingError, setEnablingError] = useState(null);

  const fetchDataCall = useMemo(
    () =>
      makeRequestBinding(
        fetching,
        fetchingError,
        setFetching,
        setFetchingError,
        defaultConfig.fetchDataStatusCode,
        { method: "GET" }
      ),
    [setFetching, setFetchingError]
  );
  const detailGetDataCall = useMemo(
    () =>
      makeRequestBinding(
        getting,
        gettingError,
        setGetting,
        setGettingError,
        defaultConfig.getDetailDataStatusCode,
        { method: "GET" }
      ),
    [setGetting, setGettingError]
  );
  const patchDataCall = useMemo(
    () =>
      makeRequestBinding(
        patching,
        patchingError,
        setPatching,
        setPatchingError,
        defaultConfig.patchDataStatusCode,
        { method: "PATCH" }
      ),
    [setPatching, setPatchingError]
  );
  const putDataCall = useMemo(
    () =>
      makeRequestBinding(
        putting,
        puttingError,
        setPutting,
        setPuttingError,
        defaultConfig.putDataStatusCode,
        { method: "PUT" }
      ),
    [setPutting, setPuttingError]
  );
  const deleteDataCall = useMemo(
    () =>
      makeRequestBinding(
        deleting,
        deletingError,
        setDeleting,
        setDeletingError,
        defaultConfig.deleteDataStatusCode,
        { method: "DELETE" }
      ),
    [setDeleting, setDeletingError]
  );
  const disableDataCall = useMemo(
    () =>
      makeRequestBinding(
        disabling,
        disablingError,
        setDisabling,
        setDeletingError,
        defaultConfig.disableDataStatusCode,
        { method: "PATCH" }
      ),
    [setDisabling, setDisablingError]
  );
  const enableDataCall = useMemo(
    () =>
      makeRequestBinding(
        disabling,
        enablingError,
        setEnabling,
        setEnablingError,
        defaultConfig.enableDataStatusCode,
        { method: "PATCH" }
      ),
    [setEnabling, setEnablingError]
  );

  const fetchData = useCallback(async (url, params) => {
    try {
      const newData = await fetchDataCall(url, params);
      const transformedNewData = await config.transformFunction(
        await newData,
        alias
      );
      await dispatch({
        type: "ADD_MULTIPLE",
        entityName,
        data: transformedNewData
      });

      return newData; //always return the answer, we never know when they need it
    } catch (e) {
      console.log(e);
      throw e;
    }
  }, []);

  const detailGetData = useCallback(async (url, params, id) => {
    try {
      const newData = await detailGetDataCall(url, params, id);
      const transformedNewData = await config.transformFunction(await newData);

      await dispatch({
        type: "ADD_SINGLE",
        entityName,
        data: transformedNewData
      });

      return newData; //always return the answer, we never know when they need it
    } catch (e) {
      console.log(e);
    }
  }, []);

  const patchData = useCallback(async (url, params, id) => {
    try {
      const newData = await patchDataCall(url, params, id);
      const transformedNewData = await config.transformFunction(await newData);
      await dispatch({
        type: "PATCH",
        entityName,
        data: transformedNewData
      });
      return newData; //always return the answer, we never know when they need it
    } catch (e) {
      console.log(e);
    }
  }, []);

  const putData = useCallback(async (url, params) => {
    try {
      const newData = await putDataCall(url, params);
      const transformedNewData = await config.transformFunction(await newData);
      await dispatch({
        type: "ADD_SINGLE",
        entityName,
        data: transformedNewData
      });
      return newData; //always return the answer, we never know when they need it
    } catch (e) {
      console.log(e);
    }
  }, []);

  const deleteData = useCallback(async (url, params, id) => {
    try {
      const deletedData = await deleteDataCall(url, params, id);
      await dispatch({
        type: "DELETE",
        entityName,
        data: deletedData
      });
      return deletedData; //always return the answer, we never know when they need it
    } catch (e) {
      console.log(e);
    }
  }, []);

  const disableData = useCallback(async (url, params, id) => {
    try {
      const updatedData = await disableDataCall(url, params, id);
      await dispatch({
        type: "PATCH",
        entityName,
        data: updatedData
      });
    } catch (e) {
      console.log(e);
    }
  }, []);

  const enableData = useCallback(async (url, params, id) => {
    try {
      const updatedData = await enableDataCall(url, params, id);
      await dispatch({
        type: "PATCH",
        entityName,
        data: updatedData
      });
    } catch (e) {
      console.log(e);
    }
  }, []);

  return {
    data: database[entityName],
    fetching,
    fetchingError,
    fetchData,
    getting,
    gettingError,
    detailGetData,
    patching,
    patchingError,
    patchData,
    putting,
    puttingError,
    putData,
    deleting,
    deletingError,
    deleteData,
    disabling,
    disablingError,
    disableData,
    enabling,
    enablingError,
    enableData
  };
};

export default useEntity;
